// SPDX-License-Identifier: MIT
// Copyright contributors to the openassistant project

import {
  VercelAiClient,
  VercelAiClientConfigureProps,
} from './vercelai-client';
import { testConnection } from '../utils/connection-test';
import { LanguageModelV1 } from 'ai';

type DeepSeekProvider = (model: string) => LanguageModelV1;
interface Module {
  createDeepSeek: (options: {
    apiKey: string;
    baseURL: string;
  }) => DeepSeekProvider;
}

const DEFAULT_DEEPSEEK_BASE_URL = 'https://api.deepseek.com/v1';

/**
 * DeepSeek Assistant LLM for Client only
 */
export class DeepSeekAssistant extends VercelAiClient {
  protected static baseURL = DEFAULT_DEEPSEEK_BASE_URL;

  protected providerInstance: DeepSeekProvider | null = null;

  protected static instance: DeepSeekAssistant | null = null;

  public static getBaseURL() {
    return DeepSeekAssistant.baseURL;
  }

  public static override configure(config: VercelAiClientConfigureProps) {
    // Check if model has changed
    const modelChanged =
      config.model && config.model !== DeepSeekAssistant.model;
    const baseURLChanged =
      config.baseURL && config.baseURL !== DeepSeekAssistant.baseURL;

    // call parent configure
    super.configure(config);
    if (config.baseURL) DeepSeekAssistant.baseURL = config.baseURL;

    // If model or baseURL changed, reset the instance to force recreation
    if (modelChanged || baseURLChanged) {
      if (DeepSeekAssistant.instance) {
        DeepSeekAssistant.instance.restart();
      }
    }
  }

  private static async loadModule(): Promise<Module> {
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore the package will be installed by the user and loaded dynamically
      return await import('@ai-sdk/deepseek');
    } catch (error) {
      throw new Error(`Failed to load @ai-sdk/deepseek: ${error}`);
    }
  }

  public static async testConnection(
    apiKey: string,
    model: string
  ): Promise<boolean> {
    const module = await this.loadModule();
    const ds = module.createDeepSeek({
      apiKey,
      baseURL: DeepSeekAssistant.baseURL,
    });
    return await testConnection(ds(model));
  }

  private constructor(module: Module) {
    super();
    this.initializeProvider(module);
  }

  private initializeProvider(module: Module) {
    if (
      DeepSeekAssistant.apiKey ||
      DeepSeekAssistant.baseURL !== DEFAULT_DEEPSEEK_BASE_URL
    ) {
      const options = {
        apiKey: DeepSeekAssistant.apiKey,
        baseURL: DeepSeekAssistant.baseURL,
      };

      this.providerInstance = module.createDeepSeek(options);

      if (!this.providerInstance) {
        throw new Error('Failed to initialize DeepSeek');
      }

      this.llm = this.providerInstance(DeepSeekAssistant.model);
    }
  }

  public static async getInstance(): Promise<DeepSeekAssistant> {
    if (!DeepSeekAssistant.instance) {
      const module = await this.loadModule();
      DeepSeekAssistant.instance = new DeepSeekAssistant(module);
    }

    if (!DeepSeekAssistant.instance.llm) {
      DeepSeekAssistant.instance.restart();
      throw new Error('DeepSeekAssistant is not initialized');
    }
    return DeepSeekAssistant.instance;
  }

  public override restart() {
    super.restart();
    // need to reset the instance so getInstance doesn't return the same instance
    this.providerInstance = null;
    DeepSeekAssistant.instance = null;
  }
}
